

  // Config constants
  const HEALTH_API_URL = 'https://api.jyswms.com/jy-health';
  const SYSTEM_HEALTH_URL = '/system-health';
  const TICKER_DURATION_SECONDS = 40;
  const REFRESH_INTERVAL_MS = 60000;

  // Baseline health data structure matching the reference video / API schema
  const DEFAULT_HEALTH_DATA = {
    status: 'healthy',
    timestamp: '2026-10-28T18:20:21Z',
    ec2: {
      status: 'healthy',
      total_instances: 1,
      running_instances: 1,
      instances: [
        {
          instance_id: 'i-050e3537a0d6a9731',
          name: 'jyswms-server',
          instance_type: 'm5.2xlarge',
          state: 'running',
          system_status: 'ok',
          instance_status: 'ok',
          cpu_utilization_pct: 12.4,
        },
      ],
    },
    rds: {
      status: 'available',
      total_databases: 1,
      available_databases: 1,
      databases: [
        {
          db_instance_identifier: 'jyswms-db',
          engine: 'postgres',
          status: 'available',
          endpoint: 'db.jyswms.internal:5432',
          connections: 14,
          cpu_utilization_pct: 7.5,
          storage_used_gb: 42,
          allocated_storage_gb: 100,
          freeable_memory_gb: 4.2,
        },
      ],
    },
    region_health: {
      status: 'ok',
      region: 'us-east-1',
      latency_ms: 64,
      http_status: 200,
    },
    netsuite_endpoints: {
      status: 'ok',
      summary: '8/8',
      total: 8,
      successful: 8,
      failed: 0,
      failed_endpoints: [],
      endpoints: [
        {
          endpoint: '/netsuite/all-sales-orders',
          status: 'ok',
          is_success: true,
          http_status: 200,
          execution_time_seconds: 201.45,
          updated_at: '2026-10-28 14:02:00',
        },
        {
          endpoint: '/netsuite/sales-orders',
          status: 'ok',
          is_success: true,
          http_status: 200,
          execution_time_seconds: 56.49,
          updated_at: '2026-10-28 14:02:00',
        },
      ],
    },
    priority_sync: {
      status: 'ok',
      not_synced: 322,
      no_label: 5,
    },
  };

  /**
   * Parse timezone-less US Eastern strings like "2026-10-13 02:47:08.291305" into a Date object (DST-aware).
   * If the string has Z or an offset (e.g. "+00:00" or "-04:00"), parse as-is.
   * Returns null on missing or malformed input without throwing.
   */
  function parseTimestamp(str) {
    if (!str || typeof str !== 'string') return null;
    const trimmed = str.trim();
    if (!trimmed) return null;

    // Has Z or timezone offset at the end (+HH:MM, -HH:MM, etc.)
    if (/[Zz]|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
      const d = new Date(trimmed);
      return isNaN(d.getTime()) ? null : d;
    }

    // Matches 'YYYY-MM-DD HH:mm:ss' or 'YYYY-MM-DDTHH:mm:ss', optional milliseconds
    const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/);
    if (!m) {
      const fallback = new Date(trimmed);
      return isNaN(fallback.getTime()) ? null : fallback;
    }

    const [, y, mo, d, h, mi, s, ms] = m;
    const year = parseInt(y, 10);
    const month = parseInt(mo, 10);
    const day = parseInt(d, 10);
    const hour = parseInt(h, 10);
    const minute = parseInt(mi, 10);
    const second = parseInt(s, 10);
    const millisecond = ms ? parseInt(ms.padEnd(3, '0').slice(0, 3), 10) : 0;

    // Use Intl.DateTimeFormat to resolve the America/New_York UTC offset dynamically
    let utcGuess = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    for (let i = 0; i < 3; i++) {
      const parts = dtf.formatToParts(new Date(utcGuess));
      const p = {};
      for (const part of parts) p[part.type] = part.value;
      let ph = parseInt(p.hour, 10);
      if (ph === 24) ph = 0;
      const formattedAsUtc = Date.UTC(
        parseInt(p.year, 10),
        parseInt(p.month, 10) - 1,
        parseInt(p.day, 10),
        ph,
        parseInt(p.minute, 10),
        parseInt(p.second, 10),
        millisecond
      );
      const targetUtc = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
      const diff = targetUtc - formattedAsUtc;
      if (diff === 0) break;
      utcGuess += diff;
    }

    return new Date(utcGuess);
  }

  /**
   * Format execution time:
   * e.g. < 60s: "12s" or "45s", >= 60s: "6m 35s" style
   */
  function formatExecutionTime(sec) {
    if (sec == null || isNaN(sec)) return '—';
    const s = Number(sec);
    if (s < 60) {
      return `${Math.round(s)}s`;
    }
    const mins = Math.floor(s / 60);
    const remSec = Math.round(s % 60);
    return `${mins}m ${remSec}s`;
  }

  /**
   * Format relative time string ("x min ago", "just now", etc.)
   */
  function formatRelativeTime(date) {
    if (!date || isNaN(date.getTime())) return '—';
    const now = new Date();
    const diffSec = Math.round((now.getTime() - date.getTime()) / 1000);
    if (diffSec < 5) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    const diffDay = Math.floor(diffHour / 24);
    return `${diffDay}d ago`;
  }

  /**
   * Format date as US Eastern string
   */
  function formatEastern(date) {
    if (!date || isNaN(date.getTime())) return '—';
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      }).format(date);
    } catch {
      return date.toLocaleString();
    }
  }

  /**
   * Map status strings to color categories: green, amber, red, gray
   * Case-insensitive with sensible fallbacks.
   */
  function getStatusCategory(status) {
    if (typeof status === 'boolean') {
      return status ? 'green' : 'red';
    }
    const s = String(status || '').toLowerCase().trim();
    if (['healthy', 'success', 'ok', 'available', 'running', 'pass'].includes(s)) return 'green';
    if (['degraded', 'not configured', 'not_configured', 'warning'].includes(s)) return 'amber';
    if (['unhealthy', 'failed', 'impaired', 'alarm', 'in_alarm', 'fail'].includes(s)) return 'red';
    if (['terminated', 'stopped', 'unknown'].includes(s)) return 'gray';
    return 'gray';
  }

  /**
   * Escape HTML to prevent XSS in modal content
   */
  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Build ticker items array from health data in the exact required order:
   * 1. EC2
   * 2. Database
   * 3. Region
   * 4. Alarms (ONLY if "alarms" key exists)
   * 5. NetSuite
   * 6. Priority sync
   * 7. Individual NetSuite endpoint items
   */
  function buildItems(data, isUnreachable = false) {
    const safeData = data && typeof data === 'object' ? data : {};
    const items = [];

    // If unreachable and explicitly empty data object passed
    if (isUnreachable && Object.keys(safeData).length === 0) {
      items.push({
        id: 'unreachable',
        label: 'HEALTH API',
        value: 'Unreachable',
        sub: 'Fetch failed',
        status: 'red',
        isValAmber: false,
        isValRed: true,
      });
      return items;
    }

    // 1. EC2
    const ec2 = safeData.ec2 || {};
    const ec2UnhealthyNotConfigured =
      ec2.status === 'unhealthy' &&
      typeof ec2.reason === 'string' &&
      ec2.reason.toLowerCase().includes('not configured');

    if (ec2UnhealthyNotConfigured) {
      items.push({
        id: 'ec2',
        label: 'EC2',
        value: 'Not configured',
        sub: 'instances running',
        status: 'amber',
        isValAmber: true,
      });
    } else {
      const running = ec2.running_instances ?? 0;
      const total = ec2.total_instances ?? 0;
      items.push({
        id: 'ec2',
        label: 'EC2',
        value: `${running} of ${total}`,
        sub: 'instances running',
        status: getStatusCategory(ec2.status || 'unknown'),
      });
    }

    // 2. Database
    const rds = safeData.rds || {};
    const rdsUnhealthyNotConfigured =
      rds.status === 'unhealthy' &&
      typeof rds.reason === 'string' &&
      rds.reason.toLowerCase().includes('not configured');

    if (rdsUnhealthyNotConfigured) {
      items.push({
        id: 'rds',
        label: 'Database',
        value: 'Not configured',
        sub: 'available',
        status: 'amber',
        isValAmber: true,
      });
    } else {
      const available = rds.available_databases ?? 0;
      const total = rds.total_databases ?? 0;
      items.push({
        id: 'rds',
        label: 'Database',
        value: `${available} of ${total}`,
        sub: 'available',
        status: getStatusCategory(rds.status || 'unknown'),
      });
    }

    // 3. Region
    const region = safeData.region_health || {};
    const latency = region.latency_ms != null ? Math.round(Number(region.latency_ms)) : 0;
    const regionName = region.region || '—';
    const httpStatus = region.http_status != null ? region.http_status : '—';
    items.push({
      id: 'region',
      label: 'Region',
      value: `${latency} ms`,
      sub: `${regionName}, HTTP ${httpStatus}`,
      status: getStatusCategory(region.status || (httpStatus === 200 ? 'ok' : 'unknown')),
    });

    // 4. Alarms: ONLY if the "alarms" key exists in data
    if ('alarms' in safeData && safeData.alarms != null) {
      const alarms = safeData.alarms;
      const inAlarm = alarms.in_alarm_count ?? 0;
      const total = alarms.total_alarms ?? 0;
      let statusColor = getStatusCategory(alarms.status || 'ok');
      if (inAlarm > 0) statusColor = 'red';
      items.push({
        id: 'alarms',
        label: 'Alarms',
        value: `${inAlarm} in alarm`,
        sub: `${total} total`,
        status: statusColor,
        isValRed: inAlarm > 0,
      });
    }

    // 5. NetSuite
    const ns = safeData.netsuite_endpoints || {};
    const nsSummary = ns.summary || `${ns.successful ?? 0}/${ns.total ?? 0}`;
    const failedCount = ns.failed ?? (Array.isArray(ns.failed_endpoints) ? ns.failed_endpoints.length : 0);
    let nsStatusColor = getStatusCategory(ns.status || 'ok');
    if (failedCount > 0) nsStatusColor = 'red';
    items.push({
      id: 'netsuite',
      label: 'NetSuite',
      value: nsSummary,
      sub: 'endpoints passing',
      status: nsStatusColor,
      isValRed: failedCount > 0,
    });

    // 6. Priority sync
    const sync = safeData.priority_sync || {};
    const notSynced = sync.not_synced ?? 0;
    const noLabel = sync.no_label ?? 0;
    items.push({
      id: 'priority_sync',
      label: 'Priority sync',
      value: String(notSynced),
      sub: `waiting to sync · ${noLabel} no label`,
      status: getStatusCategory(sync.status || (notSynced > 0 ? 'amber' : 'ok')),
    });

    // 7. Individual NetSuite endpoint items (one item per endpoint)
    const endpoints = Array.isArray(ns.endpoints) ? ns.endpoints : [];
    endpoints.forEach((ep, idx) => {
      const epPath = ep.endpoint || `Endpoint ${idx + 1}`;
      const isSuccess = ep.is_success !== false && ep.status !== 'failed';
      const execTime = formatExecutionTime(ep.execution_time_seconds);
      const epStatusColor = isSuccess ? getStatusCategory(ep.status || 'ok') : 'red';
      items.push({
        id: `endpoint:${ep.endpoint || idx}`,
        endpointKey: ep.endpoint || String(idx),
        label: epPath,
        value: execTime,
        sub: `HTTP ${ep.http_status != null ? ep.http_status : '—'}`,
        status: epStatusColor,
        isValRed: !isSuccess,
      });
    });

    return items;
  }

  /**
   * Get modal title and HTML content for a clicked item id
   */
  function getDetail(itemId, data) {
    const safeData = data && typeof data === 'object' ? data : {};
    if (!itemId) return { title: 'Health Details', html: '<p>No item selected.</p>' };

    // Unreachable alert detail
    if (itemId === 'unreachable') {
      return {
        title: 'Health API Status',
        html: `
          <div class="hs-notice-box" style="border-left-color: #dc2626; color: #991b1b; background-color: #fef2f2; border-color: #fecaca;">
            <strong>Health API unreachable:</strong> Could not connect to the health monitoring endpoint (${escapeHtml(HEALTH_API_URL)}).
            The ticker is displaying cached information or fallback values.
          </div>
        `,
      };
    }

    // 1. EC2
    if (itemId === 'ec2') {
      const ec2 = safeData.ec2 || {};
      const isNotConfigured =
        ec2.status === 'unhealthy' &&
        typeof ec2.reason === 'string' &&
        ec2.reason.toLowerCase().includes('not configured');

      if (isNotConfigured) {
        return {
          title: 'EC2 Instances',
          html: `
            <div class="hs-notice-box">
              <strong>Status:</strong> ${escapeHtml(ec2.reason || 'AWS credentials not configured')}
            </div>
            <p>AWS credentials are not configured on this host, so EC2 instances cannot be monitored.</p>
          `,
        };
      }

      const instances = Array.isArray(ec2.instances) ? ec2.instances : [];
      let rowsHtml = '';
      if (instances.length === 0) {
        rowsHtml = '<tr><td colspan="7" style="text-align:center; color:#64748b;">No EC2 instances found.</td></tr>';
      } else {
        rowsHtml = instances
          .map((inst) => {
            const name = inst.name || '—';
            const id = inst.instance_id || '—';
            const type = inst.instance_type || '—';
            const state = inst.state || inst.status || '—';
            const stateColor = getStatusCategory(state);
            const sysCheck = inst.system_status || inst.system_check || '—';
            const instCheck = inst.instance_status || inst.instance_check || '—';
            const cpu = inst.cpu_utilization_pct != null ? `${Number(inst.cpu_utilization_pct).toFixed(1)}%` : '—';
            return `
              <tr>
                <td><strong>${escapeHtml(name)}</strong></td>
                <td><code style="font-size:0.75rem;">${escapeHtml(id)}</code></td>
                <td>${escapeHtml(type)}</td>
                <td><span class="hs-badge hs-badge-${stateColor}">${escapeHtml(state)}</span></td>
                <td>${escapeHtml(sysCheck)}</td>
                <td>${escapeHtml(instCheck)}</td>
                <td>${escapeHtml(cpu)}</td>
              </tr>
            `;
          })
          .join('');
      }

      return {
        title: 'EC2 Instances',
        html: `
          <div class="hs-kv-list">
            <div class="hs-kv-card">
              <div class="hs-kv-label">Running Instances</div>
              <div class="hs-kv-value">${ec2.running_instances ?? 0} of ${ec2.total_instances ?? 0}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Overall Status</div>
              <div class="hs-kv-value"><span class="hs-badge hs-badge-${getStatusCategory(ec2.status)}">${escapeHtml(ec2.status || 'unknown')}</span></div>
            </div>
          </div>
          <table class="hs-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Type</th>
                <th>State</th>
                <th>System Check</th>
                <th>Instance Check</th>
                <th>CPU %</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        `,
      };
    }

    // 2. Database (RDS)
    if (itemId === 'rds') {
      const rds = safeData.rds || {};
      const isNotConfigured =
        rds.status === 'unhealthy' &&
        typeof rds.reason === 'string' &&
        rds.reason.toLowerCase().includes('not configured');

      if (isNotConfigured) {
        return {
          title: 'RDS Databases',
          html: `
            <div class="hs-notice-box">
              <strong>Status:</strong> ${escapeHtml(rds.reason || 'AWS credentials not configured')}
            </div>
            <p>AWS credentials are not configured on this host, so RDS database metrics cannot be loaded.</p>
          `,
        };
      }

      const dbs = Array.isArray(rds.databases) ? rds.databases : [];
      let rowsHtml = '';
      if (dbs.length === 0) {
        rowsHtml = '<tr><td colspan="8" style="text-align:center; color:#64748b;">No databases found.</td></tr>';
      } else {
        rowsHtml = dbs
          .map((db) => {
            const id = db.db_instance_identifier || db.name || '—';
            const engine = db.engine || '—';
            const status = db.status || '—';
            const statusColor = getStatusCategory(status);
            const endpoint = db.endpoint || '—';
            const conns = db.connections != null ? db.connections : (db.current_connections ?? '—');
            const cpu = db.cpu_utilization_pct != null ? `${Number(db.cpu_utilization_pct).toFixed(1)}%` : '—';
            const storage = db.allocated_storage_gb != null
              ? `${db.storage_used_gb != null ? db.storage_used_gb + ' / ' : ''}${db.allocated_storage_gb} GB`
              : '—';
            const mem = db.freeable_memory_gb != null ? `${Number(db.freeable_memory_gb).toFixed(2)} GB` : '—';
            return `
              <tr>
                <td><strong>${escapeHtml(id)}</strong></td>
                <td>${escapeHtml(engine)}</td>
                <td><span class="hs-badge hs-badge-${statusColor}">${escapeHtml(status)}</span></td>
                <td style="font-size:0.75rem; word-break:break-all;">${escapeHtml(endpoint)}</td>
                <td>${escapeHtml(String(conns))}</td>
                <td>${escapeHtml(cpu)}</td>
                <td>${escapeHtml(storage)}</td>
                <td>${escapeHtml(mem)}</td>
              </tr>
            `;
          })
          .join('');
      }

      return {
        title: 'RDS Databases',
        html: `
          <div class="hs-kv-list">
            <div class="hs-kv-card">
              <div class="hs-kv-label">Available Databases</div>
              <div class="hs-kv-value">${rds.available_databases ?? 0} of ${rds.total_databases ?? 0}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Overall Status</div>
              <div class="hs-kv-value"><span class="hs-badge hs-badge-${getStatusCategory(rds.status)}">${escapeHtml(rds.status || 'unknown')}</span></div>
            </div>
          </div>
          <table class="hs-table">
            <thead>
              <tr>
                <th>Identifier</th>
                <th>Engine</th>
                <th>Status</th>
                <th>Endpoint</th>
                <th>Connections</th>
                <th>CPU %</th>
                <th>Storage</th>
                <th>Free Memory</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        `,
      };
    }

    // 3. Region
    if (itemId === 'region') {
      const region = safeData.region_health || {};
      const statusColor = getStatusCategory(region.status || 'ok');
      return {
        title: 'AWS Region Health',
        html: `
          <div class="hs-kv-list">
            <div class="hs-kv-card">
              <div class="hs-kv-label">AWS Region</div>
              <div class="hs-kv-value">${escapeHtml(region.region || '—')}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Latency</div>
              <div class="hs-kv-value">${region.latency_ms != null ? Math.round(Number(region.latency_ms)) + ' ms' : '—'}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">HTTP Status</div>
              <div class="hs-kv-value">${escapeHtml(String(region.http_status ?? '—'))}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Status</div>
              <div class="hs-kv-value"><span class="hs-badge hs-badge-${statusColor}">${escapeHtml(region.status || 'unknown')}</span></div>
            </div>
          </div>
        `,
      };
    }

    // 4. Alarms
    if (itemId === 'alarms') {
      const alarms = safeData.alarms || {};
      const inAlarmList = Array.isArray(alarms.in_alarm) ? alarms.in_alarm : [];
      let alarmRowsHtml = '';
      if (inAlarmList.length === 0) {
        alarmRowsHtml = '<tr><td colspan="3" style="text-align:center; color:#15803d; padding:1rem 0;">No active alarms. Everything is normal.</td></tr>';
      } else {
        alarmRowsHtml = inAlarmList
          .map((al) => {
            const name = al.alarm_name || al.name || 'Unnamed Alarm';
            const reason = al.state_reason || al.reason || '—';
            const state = al.state_value || al.state || 'ALARM';
            return `
              <tr>
                <td><strong>${escapeHtml(name)}</strong></td>
                <td><span class="hs-badge hs-badge-red">${escapeHtml(state)}</span></td>
                <td style="font-size:0.75rem;">${escapeHtml(reason)}</td>
              </tr>
            `;
          })
          .join('');
      }

      return {
        title: 'CloudWatch Alarms',
        html: `
          <div class="hs-kv-list">
            <div class="hs-kv-card">
              <div class="hs-kv-label">Alarms Triggered</div>
              <div class="hs-kv-value" style="${(alarms.in_alarm_count ?? 0) > 0 ? 'color:#dc2626;' : ''}">
                ${alarms.in_alarm_count ?? 0} in alarm
              </div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Total Monitored</div>
              <div class="hs-kv-value">${alarms.total_alarms ?? 0} total</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Summary</div>
              <div class="hs-kv-value" style="font-size:0.8125rem;">${escapeHtml(alarms.summary || '—')}</div>
            </div>
          </div>
          <h4 style="margin:1rem 0 0.5rem; font-size:0.875rem;">Active Alarms</h4>
          <table class="hs-table">
            <thead>
              <tr>
                <th>Alarm Name</th>
                <th>State</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>${alarmRowsHtml}</tbody>
          </table>
        `,
      };
    }

    // 5. NetSuite summary
    if (itemId === 'netsuite') {
      const ns = safeData.netsuite_endpoints || {};
      const endpoints = Array.isArray(ns.endpoints) ? ns.endpoints : [];
      const rowsHtml = endpoints
        .map((ep) => {
          const isSuccess = ep.is_success !== false && ep.status !== 'failed';
          const statusBadge = isSuccess
            ? '<span class="hs-badge hs-badge-green">PASS</span>'
            : '<span class="hs-badge hs-badge-red">FAIL</span>';
          const execTime = formatExecutionTime(ep.execution_time_seconds);
          const updatedDate = parseTimestamp(ep.updated_at);
          const updatedStr = updatedDate ? `${formatEastern(updatedDate)} (${formatRelativeTime(updatedDate)})` : '—';
          return `
            <tr>
              <td style="word-break:break-all;"><strong>${escapeHtml(ep.endpoint || '—')}</strong></td>
              <td>${statusBadge}</td>
              <td>${escapeHtml(String(ep.http_status ?? '—'))}</td>
              <td>${escapeHtml(execTime)}</td>
              <td style="font-size:0.75rem; white-space:nowrap;">${escapeHtml(updatedStr)}</td>
            </tr>
          `;
        })
        .join('');

      return {
        title: 'NetSuite Endpoints Summary',
        html: `
          <div class="hs-kv-list">
            <div class="hs-kv-card">
              <div class="hs-kv-label">Summary</div>
              <div class="hs-kv-value">${escapeHtml(ns.summary || `${ns.successful ?? 0}/${ns.total ?? 0}`)}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Passing</div>
              <div class="hs-kv-value" style="color:#15803d;">${ns.successful ?? 0} successful</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Failing</div>
              <div class="hs-kv-value" style="${(ns.failed ?? 0) > 0 ? 'color:#dc2626;' : ''}">${ns.failed ?? 0} failed</div>
            </div>
          </div>
          <table class="hs-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Status</th>
                <th>HTTP</th>
                <th>Execution</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>${rowsHtml || '<tr><td colspan="5" style="text-align:center; color:#64748b;">No endpoints recorded.</td></tr>'}</tbody>
          </table>
        `,
      };
    }

    // 6. Priority sync
    if (itemId === 'priority_sync') {
      const sync = safeData.priority_sync || {};
      const statusColor = getStatusCategory(sync.status || 'ok');
      return {
        title: 'Priority Sync Health',
        html: `
          <div class="hs-kv-list">
            <div class="hs-kv-card">
              <div class="hs-kv-label">Waiting to Sync</div>
              <div class="hs-kv-value">${sync.not_synced ?? 0}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">No Label</div>
              <div class="hs-kv-value">${sync.no_label ?? 0}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Status</div>
              <div class="hs-kv-value"><span class="hs-badge hs-badge-${statusColor}">${escapeHtml(sync.status || 'unknown')}</span></div>
            </div>
          </div>
          <p style="font-size:0.8125rem; color:#64748b; margin-top:0.5rem;">
            Tracks pending order synchronizations between JYSWMS and external sales channels.
          </p>
        `,
      };
    }

    // 7. Single NetSuite endpoint
    if (itemId.startsWith('endpoint:')) {
      const epKey = itemId.replace('endpoint:', '');
      const ns = safeData.netsuite_endpoints || {};
      const endpoints = Array.isArray(ns.endpoints) ? ns.endpoints : [];
      const ep = endpoints.find((e, idx) => (e.endpoint || String(idx)) === epKey) || {};
      const isSuccess = ep.is_success !== false && ep.status !== 'failed';
      const statusColor = isSuccess ? 'green' : 'red';
      const execTime = formatExecutionTime(ep.execution_time_seconds);
      const updatedDate = parseTimestamp(ep.updated_at);
      const updatedEastern = updatedDate ? formatEastern(updatedDate) : '—';
      const updatedRelative = updatedDate ? formatRelativeTime(updatedDate) : '—';

      return {
        title: 'NetSuite Endpoint Details',
        html: `
          <div class="hs-kv-list">
            <div class="hs-kv-card" style="grid-column: 1 / -1;">
              <div class="hs-kv-label">Endpoint Path</div>
              <div class="hs-kv-value" style="font-size:0.875rem; word-break:break-all;">${escapeHtml(ep.endpoint || epKey)}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Status</div>
              <div class="hs-kv-value"><span class="hs-badge hs-badge-${statusColor}">${escapeHtml(ep.status || (isSuccess ? 'ok' : 'failed'))}</span></div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">HTTP Status</div>
              <div class="hs-kv-value">${escapeHtml(String(ep.http_status ?? '—'))}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Execution Time</div>
              <div class="hs-kv-value">${escapeHtml(execTime)}</div>
            </div>
            <div class="hs-kv-card">
              <div class="hs-kv-label">Last Updated</div>
              <div class="hs-kv-value" style="font-size:0.8125rem;">
                ${escapeHtml(updatedEastern)}<br />
                <span style="font-size:0.75rem; color:#64748b; font-weight:normal;">(${escapeHtml(updatedRelative)})</span>
              </div>
            </div>
          </div>
        `,
      };
    }

    return {
      title: 'Health Details',
      html: `<p>Details not available for item: ${escapeHtml(itemId)}</p>`,
    };
  }

  export function initHealthTicker(customMount) {
    try {
      const mount = customMount || document.getElementById('hs-ticker-mount');
      if (!mount) return;
      if (mount.querySelector('.hs-ticker')) return; // already mounted

      // State: Seed with baseline health data so real cards render immediately and scroll
      let currentData = DEFAULT_HEALTH_DATA;
      let lastGoodData = DEFAULT_HEALTH_DATA;
      let activeItemId = null;
      let isPaused = false;
      let abortController = null;
      let lastFocusedElement = null;
      let prevBodyOverflow = '';

      // Create ticker container
      const ticker = document.createElement('div');
      ticker.className = 'hs-ticker';
      ticker.setAttribute('role', 'region');
      ticker.setAttribute('aria-label', 'System health status ticker');

      const viewport = document.createElement('div');
      viewport.className = 'hs-viewport';

      const track = document.createElement('div');
      track.className = 'hs-track';

      viewport.appendChild(track);
      ticker.appendChild(viewport);

      // Stationary round yellow Pause/play toggle button at far right
      const pauseBtn = document.createElement('button');
      pauseBtn.type = 'button';
      pauseBtn.className = 'hs-pause-btn';
      pauseBtn.setAttribute('aria-label', 'Pause scrolling');
      pauseBtn.setAttribute('title', 'Pause scrolling');
      pauseBtn.textContent = '⏸';

      pauseBtn.addEventListener('click', () => {
        isPaused = !isPaused;
        if (isPaused) {
          ticker.classList.add('hs-paused');
          pauseBtn.textContent = '▶';
          pauseBtn.setAttribute('aria-label', 'Resume scrolling');
          pauseBtn.setAttribute('title', 'Resume scrolling');
        } else {
          ticker.classList.remove('hs-paused');
          pauseBtn.textContent = '⏸';
          pauseBtn.setAttribute('aria-label', 'Pause scrolling');
          pauseBtn.setAttribute('title', 'Pause scrolling');
        }
      });

      ticker.appendChild(pauseBtn);
      mount.appendChild(ticker);

      // Render baseline health cards immediately so scrolling starts right away
      updateTrack(buildItems(DEFAULT_HEALTH_DATA));

      // Create Modal DOM
      const modal = document.createElement('div');
      modal.id = 'hs-modal';
      modal.className = 'hs-modal-backdrop';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'hs-modal-title');
      modal.hidden = true;

      const modalBox = document.createElement('div');
      modalBox.className = 'hs-modal-box';
      modalBox.addEventListener('click', (e) => e.stopPropagation());

      const modalHeader = document.createElement('div');
      modalHeader.className = 'hs-modal-header';

      const modalTitle = document.createElement('h2');
      modalTitle.id = 'hs-modal-title';
      modalTitle.className = 'hs-modal-title';
      modalTitle.textContent = 'System Health Details';

      const modalClose = document.createElement('button');
      modalClose.type = 'button';
      modalClose.className = 'hs-modal-close';
      modalClose.setAttribute('aria-label', 'Close details modal');
      modalClose.innerHTML = '&times;';

      modalHeader.appendChild(modalTitle);
      modalHeader.appendChild(modalClose);

      const modalBody = document.createElement('div');
      modalBody.id = 'hs-modal-body';
      modalBody.className = 'hs-modal-body';

      const modalFooter = document.createElement('div');
      modalFooter.className = 'hs-modal-footer';

      const lastChecked = document.createElement('span');
      lastChecked.id = 'hs-modal-last-checked';
      lastChecked.className = 'hs-modal-last-checked';
      lastChecked.textContent = 'Last checked: —';

      const modalActions = document.createElement('div');
      modalActions.className = 'hs-modal-actions';

      const btnRefresh = document.createElement('button');
      btnRefresh.type = 'button';
      btnRefresh.className = 'btn btn-primary hs-btn-refresh';
      btnRefresh.textContent = 'Refresh now';

      modalActions.appendChild(btnRefresh);

      if (SYSTEM_HEALTH_URL && SYSTEM_HEALTH_URL.trim() !== '') {
        const btnViewDetails = document.createElement('a');
        btnViewDetails.href = SYSTEM_HEALTH_URL;
        btnViewDetails.target = '_blank';
        btnViewDetails.rel = 'noopener noreferrer';
        btnViewDetails.className = 'btn hs-btn-view-details';
        btnViewDetails.textContent = 'View details';
        modalActions.appendChild(btnViewDetails);
      }

      modalFooter.appendChild(lastChecked);
      modalFooter.appendChild(modalActions);

      modalBox.appendChild(modalHeader);
      modalBox.appendChild(modalBody);
      modalBox.appendChild(modalFooter);
      modal.appendChild(modalBox);
      document.body.appendChild(modal);

      // Event handlers for modal
      function openModal(itemId) {
        activeItemId = itemId;
        lastFocusedElement = document.activeElement;
        ticker.classList.add('hs-modal-open');
        prevBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        renderModalContent();
        modal.hidden = false;
        modalClose.focus();
      }

      function closeModal() {
        modal.hidden = true;
        activeItemId = null;
        ticker.classList.remove('hs-modal-open');
        document.body.style.overflow = prevBodyOverflow;
        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
          lastFocusedElement.focus();
        }
      }

      function renderModalContent() {
        if (!activeItemId) return;
        const dataToUse = currentData || lastGoodData || DEFAULT_HEALTH_DATA;
        const detail = getDetail(activeItemId, dataToUse);
        modalTitle.textContent = detail.title;
        modalBody.innerHTML = detail.html;

        const tsDate = parseTimestamp(dataToUse.timestamp);
        if (tsDate) {
          lastChecked.textContent = `Last checked: ${formatEastern(tsDate)}`;
        } else {
          lastChecked.textContent = `Last checked: ${formatEastern(new Date())}`;
        }
      }

      modalClose.addEventListener('click', closeModal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
      });

      document.addEventListener('keydown', (e) => {
        if (!modal.hidden && e.key === 'Escape') {
          closeModal();
        }
      });

      btnRefresh.addEventListener('click', () => {
        btnRefresh.disabled = true;
        btnRefresh.textContent = 'Refreshing...';
        fetchHealth().finally(() => {
          btnRefresh.disabled = false;
          btnRefresh.textContent = 'Refresh now';
        });
      });

      // Item click delegation on track
      track.addEventListener('click', (e) => {
        const itemBtn = e.target.closest('.hs-item');
        if (itemBtn) {
          const itemId = itemBtn.getAttribute('data-item-id');
          if (itemId) openModal(itemId);
        }
      });

      // Track DOM update (in place, seamless continuous flex loop)
      function updateTrack(items) {
        if (!items || items.length === 0) return;

        // Ensure seamless looping: duplicate the complete sequence so second half is exact content duplicate
        // Repeat items sequence enough times so first half fills ticker width without blank gaps
        const reps = items.length < 5 ? Math.ceil(5 / items.length) : 1;
        let firstHalf = [];
        for (let r = 0; r < reps; r++) {
          firstHalf = firstHalf.concat(items);
        }
        const fullList = [...firstHalf, ...firstHalf];

        const existingButtons = Array.from(track.querySelectorAll('.hs-item'));

        // If count matches, update existing DOM elements IN PLACE without restarting animation
        if (existingButtons.length === fullList.length && existingButtons.length > 0) {
          existingButtons.forEach((btn, i) => {
            const it = fullList[i];
            btn.setAttribute('data-item-id', it.id);
            btn.className = `hs-item hs-status-${it.status}${it.isValAmber ? ' hs-val-amber' : ''}${it.isValRed ? ' hs-val-red' : ''}`;

            const labelText = btn.querySelector('.hs-item-label-text');
            const valEl = btn.querySelector('.hs-item-value');
            const subEl = btn.querySelector('.hs-item-sub');

            if (labelText && labelText.textContent !== it.label) labelText.textContent = it.label;
            if (valEl && valEl.textContent !== it.value) valEl.textContent = it.value;
            if (subEl && subEl.textContent !== it.sub) subEl.textContent = it.sub;
          });
        } else {
          // Rebuild items only if item count changed
          const fragment = document.createDocumentFragment();
          fullList.forEach((it) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = `hs-item hs-status-${it.status}${it.isValAmber ? ' hs-val-amber' : ''}${it.isValRed ? ' hs-val-red' : ''}`;
            btn.setAttribute('data-item-id', it.id);

            const labelSpan = document.createElement('span');
            labelSpan.className = 'hs-item-label';

            const dotSpan = document.createElement('span');
            dotSpan.className = 'hs-item-dot';
            dotSpan.textContent = '● ';

            const labelText = document.createElement('span');
            labelText.className = 'hs-item-label-text';
            labelText.textContent = it.label;

            labelSpan.appendChild(dotSpan);
            labelSpan.appendChild(labelText);

            const valSpan = document.createElement('span');
            valSpan.className = 'hs-item-value';
            valSpan.textContent = it.value;

            const subSpan = document.createElement('span');
            subSpan.className = 'hs-item-sub';
            subSpan.textContent = it.sub;

            btn.appendChild(labelSpan);
            btn.appendChild(valSpan);
            btn.appendChild(subSpan);
            fragment.appendChild(btn);
          });
          track.innerHTML = '';
          track.appendChild(fragment);
        }
      }

      // Fetch health data
      async function fetchHealth() {
        if (abortController) {
          abortController.abort();
        }
        abortController = new AbortController();

        try {
          const res = await fetch(HEALTH_API_URL, {
            signal: abortController.signal,
            cache: 'no-cache',
          });

          if (!res.ok) {
            throw new Error(`HTTP error status ${res.status}`);
          }

          const json = await res.json();
          currentData = json;
          lastGoodData = json;

          const items = buildItems(json, false);
          updateTrack(items);

          if (!modal.hidden && activeItemId) {
            renderModalContent();
          }
        } catch (err) {
          if (err.name === 'AbortError') return;

          console.warn('[HealthTicker] Fetch failed:', err);

          // On failure: Preserve health data and keep cards scrolling seamlessly without error badge
          currentData = lastGoodData || DEFAULT_HEALTH_DATA;
          const items = buildItems(currentData, false);
          updateTrack(items);

          if (!modal.hidden && activeItemId) {
            renderModalContent();
          }
        } finally {
          abortController = null;
        }
      }

      // Initial fetch + interval every 60s
      fetchHealth();
      const refreshInterval = setInterval(() => {
        fetchHealth();
      }, REFRESH_INTERVAL_MS);

      return function cleanup() {
        if (abortController) {
          abortController.abort();
        }
        if (refreshInterval) {
          clearInterval(refreshInterval);
        }
        if (modal && modal.parentNode) {
          modal.parentNode.removeChild(modal);
        }
        if (ticker && ticker.parentNode) {
          ticker.parentNode.removeChild(ticker);
        }
      };
    } catch (e) {
      console.error('[HealthTicker] Initialization failed:', e);
      return null;
    }
  }


export {
  parseTimestamp,
  formatExecutionTime,
  formatRelativeTime,
  formatEastern,
  getStatusCategory,
  buildItems,
  getDetail,
  HEALTH_API_URL,
  SYSTEM_HEALTH_URL,
  TICKER_DURATION_SECONDS,
  REFRESH_INTERVAL_MS,
  DEFAULT_HEALTH_DATA,
};
